import { Grid, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getFundraiserById } from 'src/shared/services/fundraiserService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FundraiserTypeEnum } from 'src/modules/gift/models/fundraiserType.enum';
import { FundraiserResponse } from '../models/fundraiserResponse';
import { FundraiserSettingsDetailSection } from '../components/fundraiserSettingsDetailSection';
import { FundraiserSettingsEmbedCode } from '../components/fundraiserSettingsEmbedCode';
import { FundraiserGiftCertificateTemplates } from '../components/fundraiserGiftCertificateTemplates';
import { FundraiserAutogiroReminders } from '../components/fundraiserAutogiroReminders';

export const FundraiserSettingsDetailPage = () => {
  const { t } = useTranslation(['fundraiser']);
  const params = useParams();

  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(true);
  const [fundraiserResponse, setFundraiserResponse] = useState<FundraiserResponse>();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getFundraiserById(params.id);
        if (isMounted()) {
          ConsoleHelper.log('fundraiser RES: ', result.data);
          setFundraiserResponse(result.data);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.error(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    if (params.id) {
      fetchData();
    }
  }, [isMounted, params.id]);

  return (
    <FormLoaderWrapper loading={loading}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MainContentHeader title={fundraiserResponse?.heading ?? ''} />
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Grid spacing={3} container>
              <Grid item xs={2}>
                <Typography sx={{ fontWeight: 600 }}>{t('fundraiser:header')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography>{fundraiserResponse?.heading}</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography sx={{ fontWeight: 600 }}>{t('fundraiser:dateCreated')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography>{DateHelper.getDateTime(fundraiserResponse?.createdDate)}</Typography>
              </Grid>
              {fundraiserResponse?.fundraiserSettingData && (
                <Grid item xs={12}>
                  <FundraiserSettingsDetailSection settingData={fundraiserResponse?.fundraiserSettingData} />
                </Grid>
              )}
              {/* Gåvobladsmallar för Minnesgåva och Högtidsgåva */}
              {(fundraiserResponse?.type === FundraiserTypeEnum.Minnesgåva || fundraiserResponse?.type === FundraiserTypeEnum.Högtidsgåva) && (
                <Grid item xs={12}>
                  <FundraiserGiftCertificateTemplates fundraiserId={fundraiserResponse.fundraiser_id} />
                </Grid>
              )}
              {/* Section for embedded code individual gift when type 1 & 2 */}
              {(fundraiserResponse?.type === FundraiserTypeEnum.Gåva || fundraiserResponse?.type === FundraiserTypeEnum.Minnesgåva) && (
                <FundraiserSettingsEmbedCode fundraiserResponse={fundraiserResponse} embedType='individual' />
              )}
              {/* Extra section for embedded code donor gift when type 2 */}
              {fundraiserResponse?.type === FundraiserTypeEnum.Gåva && (
                <>
                  <FundraiserSettingsEmbedCode fundraiserResponse={fundraiserResponse} embedType='donor' />
                  {/* Autogiro-påminnelser för månadsgivare */}
                  <FundraiserAutogiroReminders fundraiserResponse={fundraiserResponse} />
                </>
              )}
              {/* Type 3 has no embed code yet */}
              {fundraiserResponse?.type === FundraiserTypeEnum.Högtidsgåva && (
                <FundraiserSettingsEmbedCode fundraiserResponse={fundraiserResponse} embedType='individual' />
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </FormLoaderWrapper>
  );
};
